import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search, Handshake, BookOpen, Package } from "lucide-react";
import { useSite } from "@/lib/siteDil";
import { UrunKarti } from "@/components/site/SiteParca";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const no = (i) => String(i + 1).padStart(2, "0");

const Resim = ({ id, className = "" }) => {
  const [hata, setHata] = useState(false);
  if (!id || hata) return <div className={`flex items-center justify-center text-slate-300 ${className}`}><Package className="w-10 h-10" /></div>;
  return <img src={`${API}/urun-gorsel/${id}`} alt="" loading="lazy" onError={() => setHata(true)} className={`object-contain mix-blend-multiply ${className}`} />;
};

const Filtre = ({ ic, t, yol }) => {
  const nav = useNavigate();
  const [f, setF] = useState({ grup: "", marka: "", q: "" });
  const markalar = (ic?.gruplar || []).filter((g) => !f.grup || g.ad_tr === f.grup).flatMap((g) => g.markalar.map((m) => m.ad));
  const ara = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(Object.entries(f).filter(([, v]) => v));
    nav(yol(`/urunler${p.toString() ? `?${p}` : ""}`));
  };
  const sec = "w-full h-11 rounded-sm bg-white text-slate-900 text-sm px-3 border border-slate-300 focus:outline-none focus:border-[var(--s-accent)]";
  return (
    <form onSubmit={ara} className="s-panel rounded-lg bg-white/95 text-slate-900 p-5 sm:p-6 space-y-3 shadow-2xl shadow-black/30" data-testid="site-filtre">
      <div className="text-sm font-extrabold uppercase tracking-wide">{t.aramaBaslik}</div>
      <select data-testid="site-filtre-marka" className={sec} value={f.marka} onChange={(e) => setF({ ...f, marka: e.target.value })}>
        <option value="">{t.markalar}</option>{[...new Set(markalar)].sort().map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <select data-testid="site-filtre-grup" className={sec} value={f.grup} onChange={(e) => setF({ ...f, grup: e.target.value, marka: "" })}>
        <option value="">{t.urunGruplari}</option>{(ic?.gruplar || []).map((g) => <option key={g.ad_tr} value={g.ad_tr}>{g.ad}</option>)}
      </select>
      <input data-testid="site-filtre-q" className={sec} placeholder={t.filtreYer} value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />
      <button data-testid="site-filtre-ara" className="w-full h-12 rounded-sm bg-[var(--s-accent)] hover:bg-[var(--s-accent-h)] text-white font-bold uppercase tracking-wide inline-flex items-center justify-center gap-2 transition-colors"><Search className="w-4 h-4" />{t.ara}</button>
    </form>
  );
};

const Hero = (s) => (
  <section className="relative overflow-hidden bg-slate-950 text-white" data-testid="site-hero">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_40%,var(--s-primary),transparent_55%)] opacity-70" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-20 grid lg:grid-cols-[1fr_400px] gap-10 items-center">
      <div className="space-y-6 s-giris">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]" data-testid="site-hero-baslik">{s.ic?.metin.hero_baslik}</h1>
        <p className="text-base text-slate-300 max-w-xl">{s.ic?.metin.hero_alt}</p>
        <div className="flex flex-wrap gap-8 pt-2">
          {[[s.ic?.istatistik.grup, s.t.grup], [s.ic?.istatistik.urun, s.t.urun], [s.ic?.istatistik.marka, s.t.marka]].map(([n, l], i) => (
            <div key={i} className="border-s-2 border-[var(--s-accent)] ps-3" data-testid={`site-sayi-${i}`}><div className="text-3xl font-extrabold">{n ?? "—"}</div><div className="text-xs uppercase tracking-wider text-slate-400">{l}</div></div>))}
        </div>
      </div>
      <Filtre {...s} />
    </div>
  </section>
);

const Kart = ({ i, ad, adet, gorseller, to, t, testid }) => (
  <Link to={to} data-testid={testid} style={{ animationDelay: `${(i % 12) * 40}ms` }} className="s-giris group bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-[var(--s-primary)] hover:shadow-lg transition-[border-color,box-shadow]">
    <div className="s-foto relative grid grid-cols-2 h-40 bg-slate-50 divide-x divide-dashed divide-slate-200">
      <span className="absolute top-2 start-3 text-xs font-bold text-slate-400 z-10">{no(i)}</span>
      {[0, 1].map((k) => <div key={k} className="p-3 flex items-center justify-center"><Resim id={gorseller?.[k] || gorseller?.[0]} className="w-full h-full transition-transform duration-300 group-hover:scale-105" /></div>)}
    </div>
    <div className="flex items-center justify-between gap-2 px-4 py-3">
      <div className="min-w-0"><div className="font-bold text-slate-900 text-sm truncate">{ad}</div><div className="text-[11px] text-slate-500">{adet} {t.urun}</div></div>
      <span className="w-7 h-7 shrink-0 rounded-full border border-slate-300 flex items-center justify-center group-hover:bg-[var(--s-primary)] group-hover:text-white group-hover:border-[var(--s-primary)] transition-colors"><ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" /></span>
    </div>
  </Link>
);

const Izgara = ({ children, testid }) => <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid={testid}>{children}</div>;

const markaListesi = (ic) => {
  const m = new Map();
  (ic?.gruplar || []).forEach((g) => g.markalar.forEach((x) => { const o = m.get(x.ad); m.set(x.ad, { ad: x.ad, adet: (o?.adet || 0) + x.adet, gorseller: [...(o?.gorseller || []), ...(x.gorseller || [])] }); }));
  const sec = ic?.vitrin_markalar || [];
  if (sec.length) return sec.map((a) => m.get(a)).filter(Boolean);
  return [...m.values()].sort((a, b) => (b.gorseller.length > 0) - (a.gorseller.length > 0) || b.adet - a.adet);
};

const vitrinGruplar = (ic) => {
  const l = ic?.gruplar || [];
  const sec = ic?.vitrin_gruplar || [];
  return sec.length ? sec.map((a) => l.find((g) => g.ad_tr === a)).filter(Boolean) : l;
};

const Gruplar = ({ ic, t, yol }) => <Izgara testid="site-gruplar">{vitrinGruplar(ic).map((g, i) => <Kart key={g.ad_tr} i={i} ad={g.ad} adet={g.adet} gorseller={g.gorseller} t={t} to={yol(`/urunler?grup=${encodeURIComponent(g.ad_tr)}`)} testid={`site-grup-${i}`} />)}</Izgara>;
const Markalar = ({ ic, t, yol }) => <Izgara testid="site-markalar">{markaListesi(ic).map((x, i) => <Kart key={x.ad} i={i} {...x} t={t} to={yol(`/urunler?marka=${encodeURIComponent(x.ad)}`)} testid={`site-marka-${x.ad.replace(/\s+/g, "-")}`} />)}</Izgara>;
const Yeniler = ({ ic }) => <Izgara testid="site-yeniler">{(ic?.yeniler || []).map((u, i) => <UrunKarti key={u.id} u={u} i={i} />)}</Izgara>;

const Sekmeler = (s) => {
  const [sek, setSek] = useState("gruplar");
  const l = [["gruplar", s.t.urunGruplari, Gruplar], ["markalar", s.t.markayaGore, Markalar], ["yeniler", s.t.yeniUrunler, Yeniler]];
  const Ic = l.find((x) => x[0] === sek)[2];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 space-y-6" data-testid="site-sekmeler">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200">
        <div className="flex gap-1 -mb-px overflow-x-auto">
          {l.map(([k, ad]) => <button key={k} onClick={() => setSek(k)} data-testid={`site-sekme-${k}`}
            className={`px-4 sm:px-5 py-3 text-sm font-bold uppercase tracking-wide whitespace-nowrap border-b-2 transition-colors ${sek === k ? "border-[var(--s-accent)] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>{ad}</button>)}
        </div>
        <Link to={s.yol("/urunler")} data-testid="site-tum-urunler" className="pb-3 text-sm font-semibold text-[var(--s-primary)] inline-flex items-center gap-1.5 hover:gap-2.5 transition-[gap]">{s.t.tumUrunler}<ArrowRight className="w-4 h-4 rtl:rotate-180" /></Link>
      </div>
      {sek === "gruplar" && <p className="text-sm text-slate-500 -mt-2">{s.t.grupSec}</p>}
      <Ic {...s} />
    </section>
  );
};

const Cta = ({ ic, t, yol }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 grid sm:grid-cols-2 gap-4" data-testid="site-cta">
    <Link to={yol("/kataloglar")} data-testid="site-cta-katalog" className="group rounded-lg p-6 sm:p-8 bg-slate-950 text-white space-y-2 hover:bg-slate-900 transition-colors"><BookOpen className="w-8 h-8 text-[var(--s-accent)]" /><div className="text-xl font-extrabold">{t.kataloglar}</div><p className="text-sm text-slate-400">{t.indir}</p><span className="inline-flex items-center gap-1.5 text-sm font-semibold pt-2">{t.katalogGit}<ArrowRight className="w-4 h-4 rtl:rotate-180 group-hover:translate-x-1 transition-transform" /></span></Link>
    <a href="/bayi-basvuru" data-testid="site-cta-bayi-ol" className="rounded-lg p-6 sm:p-8 bg-[var(--s-accent)] text-white space-y-2 hover:brightness-110 transition-[filter]"><Handshake className="w-8 h-8" /><div className="text-xl font-extrabold">{ic?.metin.cta_baslik}</div><p className="text-sm text-white/85">{ic?.metin.cta_metin}</p></a>
  </section>
);

export default function SiteAnasayfa() {
  const s = useSite();
  return (
    <div data-testid="site-anasayfa" className="space-y-14 pb-4">
      <Hero {...s} />
      <Sekmeler {...s} />
      <Cta {...s} />
    </div>
  );
}
